// React
import { useEffect } from "react";
// React Router
import { Route, Routes } from "react-router-dom";
// Pages
import { Home } from "@App/Pages/Partner/Ayvens/Home";
import { Garages } from "@App/Pages/Common/Garages";
import { Profile } from "@App/Pages/Common/Profile";
import { Contact } from "@App/Pages/Common/Contact";
import { Layout } from "@App/Pages/Partner/Ayvens/Layout";
import { Summary } from "@App/Pages/Common/Summary";
import { useSelector } from "react-redux";
import type { State } from "@App/state";
import i18n from "@App/utils/i18n";
import { useNavigateWithQueryParams } from "@App/hooks/useNavigateWithQueryParams";
import { Login } from "@App/Pages/Partner/Ayvens/Login";
import { AgentVehicleSelector } from "@App/Pages/Partner/Ayvens/AgentVehicleSelector";
import { FleetManagerVehicleSelector } from "@App/Pages/Partner/Ayvens/FleetManagerVehicleSelector";
import { DriverDataService } from "@App/apis/rest/ayvens/driverData/v1/driverDataService";
import { UserIdProtectedRoutes } from "@App/Pages/Partner/Ayvens/UserIdProtectedRoutes";
import { TokenProtectedRoutes } from "./TokenProtectedRoutes";
import { DriverDataRequiredRoutes } from "@App/Pages/Partner/Ayvens/DriverDataRequiredRoutes";
import { AuthService } from "@App/apis/rest/ayvens/auth/v1/authService";
import { TokenService } from "@App/apis/rest/ayvens/token/v1/tokenService";
import { Role } from "@App/types/ayvens/role";
import { PickUpAndReturnObservedRoutes } from "@App/Pages/Partner/Ayvens/PickUpAndReturnObservedRoutes";
import { GeographyService } from "@App/apis/graphql/geography/v1/geographyService";

export function Ayvens() {
  const { navigateWithQuery } = useNavigateWithQueryParams();

  const organization = useSelector((state: State) => state.orders.organization);

  // change the language
  useEffect(() => {
    i18n.changeLanguage(organization?.settings?.language);
  }, []);

  const driverDataApi = new DriverDataService(window.rest_url);
  const authApi = new AuthService(window.rest_url);
  const tokenApi = new TokenService(window.rest_url);

  const geographyApi = new GeographyService();

  return (
    <Routes>
      <Route path={"/"} element={<Layout />}>
        <Route
          path={"login"}
          element={
            <Login
              authApi={authApi}
              onSuccessfulLogin={() => navigateWithQuery("../fahrzeug", true)}
            />
          }
        />
        {/* These routes are only accessible if the userId is set, which is done after a successful login */}
        <Route element={<UserIdProtectedRoutes />}>
          <Route
            path={"fahrzeug"}
            element={
              <AgentVehicleSelector
                tokenApi={tokenApi}
                driverDataApi={driverDataApi}
                onSubmit={(token) => navigateWithQuery("..", true, { jwt: token })}
              />
            }
          />
        </Route>
        {/* These routes are only accessible with a valid token, which happens after the user selects a vehicle or when a user get's redirected from MyAyvens. */}
        <Route element={<TokenProtectedRoutes tokenApi={tokenApi} />}>
          <Route
            path={"fahrzeugliste"}
            element={
              <FleetManagerVehicleSelector
                driverDataApi={driverDataApi}
                onSubmit={(token) => navigateWithQuery("..", true, { jwt: token })}
              />
            }
          />
          {/*
                       These routes are only accessible with valid driver information, which happens after selecting a vehicle if multiple vehicles are in the token.
                       DriverDataRequiredRoutes needs an absolute path for "onDriverDataMissing" as it can be called on every page.
                    */}
          <Route
            path={"/"}
            element={
              <DriverDataRequiredRoutes
                driverDataApi={driverDataApi}
                onDriverDataMissing={(role) =>
                  navigateWithQuery(
                    role === Role.AGENT
                      ? `/partner/${organization.slug}/fahrzeug`
                      : `/partner/${organization.slug}/fahrzeugliste`,
                    true,
                  )
                }
              />
            }
          >
            <Route
              index={true}
              element={<Home onSubmit={() => navigateWithQuery("werkstaetten", true)} />}
            />
            <Route
              element={
                <PickUpAndReturnObservedRoutes
                  geographyApi={geographyApi}
                  redirectAfterStore={() => navigateWithQuery("../werkstaetten", true)}
                />
              }
            >
              <Route
                path={"werkstaetten"}
                element={<Garages onSubmit={() => navigateWithQuery("../werkstatt", true)} />}
              />
              <Route
                path={"werkstatt"}
                element={<Profile onSubmit={() => navigateWithQuery("buchen", true)} />}
              />
            </Route>
            <Route
              path={"werkstatt/buchen"}
              element={<Contact onSubmit={() => navigateWithQuery("erfolgreich", true)} />}
            />
            <Route path={"werkstatt/buchen/erfolgreich"} element={<Summary />} />
            <Route
              path={"*"}
              element={<Home onSubmit={() => navigateWithQuery("werkstaetten", true)} />}
            />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
